import { formatDateToDDMMYYYY } from '@/app/utils/helpers'
import { IBorrower } from '@/app/models/loanQuote'
import { resolveResidenceCountry } from '@/app/utils/loan/residence-address'

/**
 * Convert options from API string array format (['dc', 'ptia', 'ipt']) to
 * the object format expected by Symfony ({ DC: true, PTIA: false, ... })
 */
export function convertOptionsArrayToObject(options: string[]): Record<string, boolean> {
    return {
        DC: options.includes('dc'),
        PTIA: options.includes('ptia'),
        IPT_ITT: options.includes('ipt'),
        IPP: options.includes('ipp'),
        PLUS: options.includes('plus'),
    }
}

const OPTION_KEY_TO_CODE: Record<string, string> = {
    DC: 'dc',
    PTIA: 'ptia',
    IPT_ITT: 'ipt',
    IPP: 'ipp',
    PLUS: 'plus',
}

// Inverse of convertOptionsArrayToObject: form object -> array of lowercase backend codes ('ipt' for IPT_ITT)
export function convertOptionsObjectToArray(options: Record<string, boolean>): string[] {
    return Object.entries(options)
        .filter(([, selected]) => selected)
        .map(([key]) => OPTION_KEY_TO_CODE[key] ?? key.toLowerCase())
}

/**
 * Garanties d'un assuré : on privilégie `options` (sélection client, écrite de façon synchrone au
 * submit donc toujours fraîche, comme le front Vue en prod), car `real_options` (ajusté assureur) est
 * recalculé plus tard côté backend (tarification) et peut être périmé d'une soumission. Fallback sur
 * `real_options` si `options` est vide (un tableau vide étant truthy en JS, on teste `.length`).
 */
export function getBorrowerSelectedOptions(
    borrower?: { real_options?: string[]; options?: string[] } | null
): string[] {
    const opts = borrower?.options ?? []
    return opts.length > 0 ? opts : borrower?.real_options ?? []
}

/**
 * Map an IBorrower (API format) to the flat format expected by Symfony's
 * updateBorrowerFromParams (LoanQuoteUpdateService.php).
 *
 * Mirrors the Vue.js BORROWER_TYPE structure from borrower_type.js.
 */
export function mapBorrowerForSubmission(
    borrower: IBorrower | any,
    borrowerType: 'main' | 'co',
    optionsFromForm?: Record<string, boolean>,
    identityOverrides?: { firstname?: string; lastname?: string; email?: string; phone?: string }
): Record<string, unknown> {
    if (!borrower) return {}

    const sportIds = Array.isArray(borrower.sport) ? borrower.sport : undefined

    // Get job - prioritize form selection (borrower.job) over API data (type_work)
    const jobId = borrower.job?.value || borrower.type_work?.id || null
    const jobLabel = borrower.job?.label || borrower.type_work?.name || null

    // Get liberal job - prioritize form selection over API data (lib_type_work)
    let liberalJobId = null
    let liberalJobLabel = null
    if (borrower.liberal_job?.value) {
        liberalJobId = borrower.liberal_job.value
        liberalJobLabel = borrower.liberal_job.label
    } else if (borrower.lib_type_work?.id) {
        liberalJobId = borrower.lib_type_work.id
        liberalJobLabel = borrower.lib_type_work.name
    }

    // Get medical job
    const medicalJobId =
        typeof borrower.medical_job === 'object' ? borrower.medical_job?.value : borrower.medical_job
    const medicalJobLabel = typeof borrower.medical_job === 'object' ? borrower.medical_job?.label : null

    // Get risk field - must be null when job_risk is false
    const hasJobRisk = borrower.job_risk ?? borrower.has_risky_job ?? false
    const riskFieldId = hasJobRisk ? borrower.risk_field?.id || borrower.risk_field?.value || null : null
    const riskFieldLabel = hasJobRisk
        ? borrower.risk_field?.name || borrower.risk_field?.label || null
        : null

    // Get family status - can be object { value } or direct value
    const familyStatusValue =
        typeof borrower.family_status === 'object' ? borrower.family_status?.value : borrower.family_status

    // Default options if none provided
    const options = optionsFromForm ?? {
        DC: false,
        PTIA: false,
        IPT_ITT: false,
        IPP: false,
        PLUS: false,
    }

    const result: Record<string, unknown> = {
        id: borrower.id || null,
        gender: borrower.prospect_info?.gender ?? null,
        firstname: borrower.prospect_info?.first_name || null,
        lastname: borrower.prospect_info?.last_name || null,
        birthdate: formatDateToDDMMYYYY(
            borrower.birthdate || borrower.prospect_info?.birthday_date || null
        ),
        email: borrower.prospect_info?.prospect?.email || null,
        phone: borrower.prospect_info?.phone || null,
        address: {
            street: borrower.prospect_info?.address_street || null,
            street_number: null,
            street_name: null,
            additional_address: null,
            zip_code: borrower.prospect_info?.address_zip_code || null,
            city: borrower.prospect_info?.address_city || null,
            country: resolveResidenceCountry(borrower.prospect_info),
            sub_area: borrower.prospect_info?.address_sub_area || null,
        },
        smoker: borrower.smoker || false,
        medical_risk: borrower.medical_risk || false,
        rejected_by_insurance: borrower.rejected_by_insurance || false,
        job: jobId ? { label: jobLabel, value: jobId } : null,
        liberal_job: liberalJobId ? { label: liberalJobLabel, value: liberalJobId } : null,
        medical_job: medicalJobId ? { label: medicalJobLabel, value: medicalJobId } : null,
        job_trips: borrower.distance > 0 || false,
        job_risk: hasJobRisk,
        job_risk_field: riskFieldId ? { label: riskFieldLabel, value: riskFieldId } : null,
        job_at_height: borrower.height || false,
        job_handler: borrower.handwork || false,
        job_clerk: borrower.clerk_job || false,
        job_exact_name: borrower.prospect_info?.name_work || null,
        // Sent as a list: the back hands this straight to setSport() on an `array` column
        risky_sport: sportIds ? sportIds.length > 0 : undefined,
        risky_sport_name: sportIds ? (sportIds.length > 0 ? sportIds : null) : undefined,
        searching_job: borrower.looking_for_work || false,
        is_expat: borrower.is_expat || false,
        type: borrowerType,
        is_lemoine: borrower.outstanding_total_credit_under_lemoine_limit || false,
        has_side_loans: borrower.side_loans || false,
        lastname_birth: borrower.prospect_info?.birth_last_name || null,
        city_birth: borrower.prospect_info?.birth_city || null,
        department_birth: borrower.prospect_info?.birth_department || null,
        family_status: familyStatusValue || null,
        cover_percentage: borrower.proportion || 100,
        options: {
            DC: options?.DC ?? false,
            PTIA: options?.PTIA ?? false,
            IPT_ITT: options?.IPT_ITT ?? false,
            IPP: options?.IPP ?? false,
            PLUS: options?.PLUS ?? false,
        },
        working_height: borrower.working_height || 0,
        annual_mileage: borrower.annual_mileage || 0,
    }

    // Apply identity overrides from form (subscription flow)
    if (identityOverrides) {
        if (identityOverrides.firstname) result.firstname = identityOverrides.firstname
        if (identityOverrides.lastname) result.lastname = identityOverrides.lastname
        if (identityOverrides.email) result.email = identityOverrides.email
        if (identityOverrides.phone) result.phone = identityOverrides.phone
    }

    return result
}
